import React from "react";

import CloseIcon from "@uui/XClose";
import { isObject } from "lodash/fp";

import type { AlertProps } from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";

import { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";

import { InfoPanelAction, Text, Wrapper } from "./styles";

interface IInfoPanelAction {
  label: string;
  onClick: (event: React.MouseEvent) => void;
}

interface InfoPanelProps extends Omit<AlertProps, "action"> {
  action?: React.ReactNode | IInfoPanelAction;
  contentVariant?: "text" | "custom";
  direction?: "column" | "row";
  iconPosition?: "left" | "top";
  textAlign?: "left" | "center";
  fullWidth?: boolean;
}

type ActionsProps = Pick<InfoPanelProps, "severity" | "action" | "onClose">;

const isActionObj = (
  action?: InfoPanelProps["action"]
): action is IInfoPanelAction => isObject(action) && "label" in action;

const Actions = ({ severity, action, onClose }: ActionsProps) => {
  if (!action && !onClose) return null;

  return (
    <Row variant="center" width="100%">
      {React.isValidElement(action) ? (
        action
      ) : isActionObj(action) ? (
        <InfoPanelAction color={severity} onClick={action.onClick}>
          {action.label}
        </InfoPanelAction>
      ) : null}
      {onClose && (
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: "shades.neutral.500", fontSize: "18px" }} />
        </IconButton>
      )}
    </Row>
  );
};

const InfoPanel = ({
  action,
  children,
  contentVariant = "text",
  direction = "row",
  iconPosition = "left",
  textAlign = "left",
  title,
  onClose,
  fullWidth = false,
  ...props
}: InfoPanelProps) => {
  const hasActions = Boolean(onClose || action);
  return (
    <Wrapper
      variant="outlined"
      iconPosition={iconPosition}
      fullWidth={fullWidth}
      action={
        hasActions ? (
          <Actions
            severity={props.severity}
            action={direction === "row" ? action : undefined}
            onClose={onClose}
          />
        ) : undefined
      }
      className={
        direction === "column" ? "column" : title ? "text-column" : undefined
      }
      {...props}
    >
      <Column flex={1} variant="left" spacing={0.5}>
        {title && (
          <Text textAlign={textAlign} width="100%">
            <Bold>{title} </Bold>
          </Text>
        )}
        {contentVariant === "text" ? (
          <Text textAlign={textAlign} width="100%">
            {children}
          </Text>
        ) : (
          children
        )}
        {direction === "column" && (
          <Actions severity={props.severity} action={action} />
        )}
      </Column>
    </Wrapper>
  );
};

export default InfoPanel;
